import {FC, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    ChipProps,
    CircularProgress,
    Collapse,
    Stack,
    Typography,
} from '@mui/material';
import {ExpandLess, ExpandMore} from '@mui/icons-material';
import {AiImageRequest, AiImageRequestStatus} from '../../types/aiImage';
import {formatKstDateTime} from '../../utils/dateTime';
import {AiImageLightbox} from './AiImageLightbox';

interface Props {
    request: AiImageRequest;
    groupName: string;
}

const STATUS_LABEL: Record<AiImageRequestStatus, string> = {
    PENDING: '대기',
    RUNNING: '진행중',
    SUCCEEDED: '완료',
    FAILED: '실패',
    CANCELED: '취소',
};

const STATUS_COLOR: Record<AiImageRequestStatus, ChipProps['color']> = {
    PENDING: 'warning',
    RUNNING: 'info',
    SUCCEEDED: 'success',
    FAILED: 'error',
    CANCELED: 'default',
};

const isActiveStatus = (s: AiImageRequestStatus) =>
    s === 'PENDING' || s === 'RUNNING';

export const AiImageRequestCard: FC<Props> = ({request, groupName}) => {
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const [errorOpen, setErrorOpen] = useState(false);

    const active = isActiveStatus(request.status);
    const succeeded = request.status === 'SUCCEEDED';

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{mb: 1}}
                >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                            size="small"
                            label={STATUS_LABEL[request.status]}
                            color={STATUS_COLOR[request.status]}
                            icon={
                                active ? (
                                    <CircularProgress size={12} sx={{color: 'inherit !important'}} />
                                ) : undefined
                            }
                        />
                        <Typography variant="caption" color="text.secondary">
                            요청 #{request.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatKstDateTime(request.createdAt)}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" variant="outlined" label={request.provider} />
                        <Chip size="small" variant="outlined" label={request.mode} />
                        <Chip size="small" variant="outlined" label={request.aspectRatio} />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`x${request.imageCount}`}
                        />
                    </Stack>
                </Stack>

                {request.userContext && (
                    <Box
                        sx={{
                            p: 1,
                            mb: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontSize: '0.78rem',
                                color: 'text.secondary',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {request.userContext}
                        </Typography>
                    </Box>
                )}

                {request.baseImageUrl && (
                    <Box sx={{mb: 1.5}}>
                        <Typography variant="caption" color="text.secondary">
                            Base image
                        </Typography>
                        <Box
                            component="img"
                            src={request.baseImageUrl}
                            alt="base"
                            sx={{
                                display: 'block',
                                mt: 0.5,
                                maxHeight: 80,
                                maxWidth: 120,
                                borderRadius: 1,
                                bgcolor: 'grey.100',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        />
                    </Box>
                )}

                {active && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{py: 1}}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                            {request.mode === 'BATCH'
                                ? '배치 처리 중 (수 분~수 시간 소요)'
                                : '생성 중...'}
                        </Typography>
                    </Stack>
                )}

                {request.status === 'FAILED' && (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Alert severity="error" sx={{flex: 1, py: 0}}>
                                생성 실패. 다시 시도해 주세요.
                            </Alert>
                            <Button
                                size="small"
                                onClick={() => setErrorOpen((v) => !v)}
                                endIcon={errorOpen ? <ExpandLess /> : <ExpandMore />}
                            >
                                상세
                            </Button>
                        </Stack>
                        <Collapse in={errorOpen}>
                            <Box
                                sx={{
                                    mt: 1,
                                    p: 1,
                                    bgcolor: 'error.light',
                                    color: 'error.contrastText',
                                    borderRadius: 1,
                                    fontFamily: 'ui-monospace, monospace',
                                    fontSize: '0.75rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {request.errorMessage ?? '에러 메시지가 없습니다.'}
                            </Box>
                        </Collapse>
                    </Box>
                )}

                {request.status === 'CANCELED' && (
                    <Alert severity="warning">취소된 요청입니다.</Alert>
                )}

                {succeeded && request.results.length > 0 && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                            gap: 1,
                        }}
                    >
                        {[...request.results]
                            .sort((a, b) => a.sequence - b.sequence)
                            .map((r, idx) => (
                                <Box
                                    key={r.id}
                                    onClick={() => setLightboxIndex(idx)}
                                    sx={{
                                        cursor: 'pointer',
                                        position: 'relative',
                                        aspectRatio: '1',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'grey.100',
                                        transition: 'transform 0.1s',
                                        '&:hover': {transform: 'scale(1.02)'},
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={r.imageUrl}
                                        alt={`result-${r.sequence}`}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`#${r.sequence}`}
                                        sx={{
                                            position: 'absolute',
                                            bottom: 4,
                                            left: 4,
                                            bgcolor: 'rgba(0,0,0,0.6)',
                                            color: 'common.white',
                                            height: 18,
                                            fontSize: '0.65rem',
                                        }}
                                    />
                                </Box>
                            ))}
                    </Box>
                )}

                {succeeded && request.results.length === 0 && (
                    <Alert severity="warning">결과 이미지가 없습니다.</Alert>
                )}
            </CardContent>

            {lightboxIndex !== null && (
                <AiImageLightbox
                    open={lightboxIndex !== null}
                    onClose={() => setLightboxIndex(null)}
                    results={[...request.results].sort((a, b) => a.sequence - b.sequence)}
                    initialIndex={lightboxIndex}
                    groupName={groupName}
                    requestId={request.id}
                />
            )}
        </Card>
    );
};
